import type { ReactNode, RefObject } from "react";
import { motion } from "framer-motion";
import DiceUI from "@/components/game/dice/DiceUI.js";
import { useGameController } from "@/components/game/GameUIContext.js";
import { useRune } from "@rosette/react_utils/src/useRune.js";
import { DiceType } from "@rosette/ts/src/royalur/model/dice/DiceType.js";
import type { PlayerType } from "@rosette/ts/src/royalur/model/PlayerType.js";
import type { GameLayoutType } from "@rosette/web_components/src/game/useGameLayout.js";
import { gameFont } from "@/components/font/gameFont.js";
import { cn } from "@rosette/ts/src/util/cn.js";


interface GameAreaProps {
    containerRef: RefObject<HTMLDivElement | null>;
    backgroundTileSrc: string;
    className: string;
    children: ReactNode;
}

/**
 * The area a game is played in, over the theme's background tile.
 */
export function GameArea({ containerRef, backgroundTileSrc, className, children }: GameAreaProps) {
    return (
        <div
            ref={containerRef}
            className={cn(
                "game-scale relative w-full h-full select-none bg-size-[20em] text-base",
                gameFont.variable,
                className,
            )}
            style={{ backgroundImage: `url(${backgroundTileSrc})` }}>

            <div className="flex flex-col w-full h-full max-w-5xl m-auto">
                {children}
            </div>
        </div>
    );
}


interface GameRowProps {
    gameLayout: GameLayoutType;
    left: ReactNode;
    board: ReactNode;
    right: ReactNode;
}

/**
 * The board with a player's state on either side.
 */
export function GameRow({ gameLayout, left, board, right }: GameRowProps) {
    const isPortrait = (gameLayout !== "landscape");
    const isSquare = (gameLayout === "square");
    const sideClassName = cn(
        "flex flex-col justify-center max-h-full",
        isSquare ? "grow basis-0" : "grow-0",
    );
    return (
        <div className={cn(
            "flex flex-row justify-center grow",
            isPortrait && "pt-[1.6em] max-h-full",
        )}>
            <div className={sideClassName}>
                {left}
            </div>

            <div className={cn(
                "flex flex-col justify-center max-h-full h-full",
                isSquare ? "grow-[1.5] basis-0" : (isPortrait ? "grow" : "grow-0"),
                // Keeps the board's aspect ratio.
                isPortrait && "max-w-[46vh]",
            )}>
                {board}
            </div>

            <div className={sideClassName}>
                {right}
            </div>
        </div>
    );
}


export function GameBottom({ children }: { children: ReactNode }) {
    return (
        <div className="flex flex-col justify-center grow-0">
            {children}
        </div>
    );
}


interface MaybeVisibleDiceUIProps {
    gameLayout: GameLayoutType;
    visible: boolean;
    playerFilter: PlayerType | null;
}

export function MaybeVisibleDiceUI({ gameLayout, visible, playerFilter }: MaybeVisibleDiceUIProps) {
    const gameController = useGameController();
    const players = useRune(gameController.getPlayers());
    const game = useRune(gameController.getGameSource().game);
    const diceType = DiceType.getByID(game.getDice().getID());
    // Only the portrait layout's dice sit below the board, overlapping it.
    const isBelowBoard = (gameLayout === "portrait");
    return (
        <div className={cn(isBelowBoard && "relative h-[10.4em] min-h-[10.4em] pointer-events-none")}>
            <motion.div
                className={cn(isBelowBoard && "absolute left-1/2 -top-[1.8em] -translate-x-1/2 pointer-events-auto")}
                initial={{
                    opacity: 0,
                    display: "none",
                }}
                animate={visible ? {
                    opacity: 1,
                    display: "",
                    transitionEnd: {
                        display: "",
                    },
                } : {
                    opacity: 0,
                    transitionEnd: {
                        display: "none",
                    },
                }}
                transition={{
                    ease: "easeInOut" as const,
                    duration: 0.2,
                }}>

                <DiceUI
                    gameLayout={gameLayout}
                    diceType={diceType}
                    leftPlayer={players.getLeftPlayer()}
                    playerFilter={playerFilter} />
            </motion.div>
        </div>
    );
}
